// frontend/src/components/FastReact/DailyRequirements.jsx
//
// 7-day matrix: one row per product_code+variant_code+lot_no, one column per
// day. SETUP and RESTOCK lines use different units (p.quantity vs
// p.num_restock_cones), so every quantity cell shows both sub-totals side by
// side as "setup|restock" rather than adding them together. Window is always
// "today + next 6 days" — no date navigation, per spec.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { fastReactAPI } from '../../api/client';
import Toast from '../Common/Toast';
import { useToast } from '../../hooks/useToast';
import { useTranslation } from '../../context/TranslationContext';
import { FiRefreshCw, FiCalendar } from 'react-icons/fi';
import './PlanoTeares.css'; // reuses pt-* classes (quick filter buttons, state msgs, spin, filter inputs)
import './DailyRequirements.css';

const STATUS_FILTERS = [
  { value: '',        label: 'Todos' },
  { value: 'SETUP',    label: 'SETUP' },
  { value: 'RESTOCK',  label: 'REABASTECER' },
];

const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const dayHeaderLabel = (iso) => {
  // iso is 'YYYY-MM-DD' — parse as local date, not UTC, so weekday/label
  // never drifts a day depending on the browser's timezone.
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  const label = dt.toLocaleDateString('pt-PT', { day: '2-digit', month: 'short' }).replace('.', '');
  return label;
};

// Zero (and anything that ROUNDS to zero at the 2-decimal precision we
// display) is treated as empty on purpose — a 0-quantity cell renders as
// the muted "—" placeholder, not a literal "0". Rounding BEFORE the
// zero-check matters: a tiny non-zero residue (e.g. 0.004, which can show
// up from floating-point SUM rounding) would pass a plain truthy check
// and then get formatted down to the string "0" anyway — this way it's
// caught up front instead of leaking through as a fake "0".
const fmtQty = (v) => {
  if (v === null || v === undefined || v === '') return '';
  const num = parseFloat(v);
  if (!Number.isFinite(num)) return '';
  const rounded = Math.round(num * 100) / 100;
  if (rounded === 0) return '';
  return rounded.toLocaleString('pt-PT', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
};

// NAV stock is always shown with exactly 2 decimals (e.g. "12,00"), and a
// real 0 stays "0,00" — unlike requirement cells, a zero stock is meaningful.
// Only a missing value (no NAV row for this product/variant/lot) is empty.
const fmtStock = (v) => {
  if (v === null || v === undefined || v === '') return '';
  const num = parseFloat(v);
  if (!Number.isFinite(num)) return '';
  return num.toLocaleString('pt-PT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// SETUP quantity and RESTOCK cones are different units, so a cell never
// sums them — it shows both, separated by "|" (e.g. "120|5", or "120|"
// when there's no restock side, or "|5" when there's no setup side).
// Returns null when there's genuinely nothing on either side, so the
// caller can fall back to the muted "—" placeholder.
const fmtPair = (setupVal, restockVal) => {
  const s = fmtQty(setupVal);
  const r = fmtQty(restockVal);
  if (!s && !r) return null;
  return `${s}|${r}`;
};

const rowKey = (r) => `${r.product_code}::${r.variant_code ?? ''}::${r.lot_no ?? ''}`;

const DailyRequirements = () => {
  const { t } = useTranslation();
  const { toasts, removeToast, toast_error } = useToast();

  const [filters, setFilters] = useState({
    status: '',
    product_code: '',
    variant_code: '',
    lot_no: '',
  });

  const [prodInput, setProdInput] = useState('');
  const [varInput, setVarInput] = useState('');
  const [lotInput, setLotInput] = useState('');
  const prodDebounce = useRef(null);
  const varDebounce = useRef(null);
  const lotDebounce = useRef(null);

  const [days, setDays] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const today = todayIso();

  // NOTE: intentionally NOT depending on toast_error here — useToast()
  // returns a new function identity on every render, so including it would
  // make loadData itself change every render, which retriggers the effect
  // below, which re-renders, forever ("Maximum update depth exceeded").
  // Same pattern PlanoTeares.jsx's loadRows uses (depends on [t] only).
  const loadData = useCallback(async (f) => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (f.status) params.status = f.status;
      if (f.product_code) params.product_code = f.product_code;
      if (f.variant_code) params.variant_code = f.variant_code;
      if (f.lot_no) params.lot_no = f.lot_no;
      const r = await fastReactAPI.getDailyRequirements(params);
      if (r.data.success) {
        setDays(r.data.days || []);
        setRows(r.data.rows || []);
      } else {
        setError(r.data.error || 'Erro ao carregar necessidades diárias.');
      }
    } catch (e) {
      setError(e.response?.data?.error || 'Erro ao carregar necessidades diárias.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(filters); }, [loadData, filters]);

  // Toast the error separately, only when `error` actually changes — keeps
  // toast_error entirely out of loadData's dependencies.
  useEffect(() => {
    if (error) toast_error(error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <div className="page-container">
      <div className="page-header page-header--compact">
        <h2>{t('daily_requirements.title', 'Necessidades Diárias')}</h2>
        <div className="pt-status-quick-filters">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.value || 'ALL'}
              type="button"
              className={`pt-quick-filter-btn ${f.value === 'RESTOCK' ? 'pt-quick-filter-btn--restock' : ''} ${filters.status === f.value ? 'pt-quick-filter-btn--active' : ''}`}
              onClick={() => handleFilterChange('status', f.value)}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="page-header-actions">
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => loadData(filters)}
            disabled={loading}
            title="Atualizar"
          >
            <FiRefreshCw size={14} className={loading ? 'pt-spin' : ''} />
          </button>
        </div>
      </div>

      <div className="list-filters">
        <div className="filters-row">
          <div className="filter-group filter-group-sm">
            <label>{t('plano_teares.product_code', 'Produto')}</label>
            <div className="filter-search-wrap">
              <input
                type="text"
                className="filter-search-input"
                placeholder={t('search', 'Pesquisar…')}
                value={prodInput}
                onChange={(e) => {
                  const v = e.target.value;
                  setProdInput(v);
                  clearTimeout(prodDebounce.current);
                  prodDebounce.current = setTimeout(() => handleFilterChange('product_code', v), 350);
                }}
              />
              {prodInput && (
                <span
                  className="filter-search-clear"
                  onClick={() => {
                    setProdInput('');
                    clearTimeout(prodDebounce.current);
                    handleFilterChange('product_code', '');
                  }}
                >
                  ×
                </span>
              )}
            </div>
          </div>

          <div className="filter-group filter-group-sm">
            <label>{t('plano_teares.variant_code', 'Variante')}</label>
            <div className="filter-search-wrap">
              <input
                type="text"
                className="filter-search-input"
                placeholder={t('search', 'Pesquisar…')}
                value={varInput}
                onChange={(e) => {
                  const v = e.target.value;
                  setVarInput(v);
                  clearTimeout(varDebounce.current);
                  varDebounce.current = setTimeout(() => handleFilterChange('variant_code', v), 350);
                }}
              />
              {varInput && (
                <span
                  className="filter-search-clear"
                  onClick={() => {
                    setVarInput('');
                    clearTimeout(varDebounce.current);
                    handleFilterChange('variant_code', '');
                  }}
                >
                  ×
                </span>
              )}
            </div>
          </div>

          <div className="filter-group filter-group-sm">
            <label>{t('plano_teares.lot_no', 'Lote')}</label>
            <div className="filter-search-wrap">
              <input
                type="text"
                className="filter-search-input"
                placeholder={t('search', 'Pesquisar…')}
                value={lotInput}
                onChange={(e) => {
                  const v = e.target.value;
                  setLotInput(v);
                  clearTimeout(lotDebounce.current);
                  lotDebounce.current = setTimeout(() => handleFilterChange('lot_no', v), 350);
                }}
              />
              {lotInput && (
                <span
                  className="filter-search-clear"
                  onClick={() => {
                    setLotInput('');
                    clearTimeout(lotDebounce.current);
                    handleFilterChange('lot_no', '');
                  }}
                >
                  ×
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="table-container">
        <div className="table-scroll">
          {loading ? (
            <div className="pt-state-msg">
              <FiRefreshCw size={20} className="pt-spin" />
              <span>A carregar…</span>
            </div>
          ) : error ? (
            <div className="pt-state-msg pt-state-error">{error}</div>
          ) : !rows.length ? (
            <div className="pt-state-msg">
              <FiCalendar size={28} style={{ color: 'var(--gray-300)' }} />
              <span>Sem necessidades para os próximos 7 dias.</span>
            </div>
          ) : (
            <table className="data-table dr-matrix">
              <thead>
                <tr>
                  <th className="dr-col-product">Produto</th>
                  <th className="dr-col-variant">Variante</th>
                  <th className="dr-col-lot">Lote</th>
                  <th className="dr-col-stock text-right" title="Stock atual no bin PROD (AP10S) — Navision">
                    Stock NAV
                  </th>
                  <th className="dr-col-overdue text-right" title="Necessidades com data de setup anterior a hoje, ainda por concluir (SETUP|REABASTECER)">
                    Atrasado
                  </th>
                  {days.map((d) => (
                    <th key={d} className={`dr-col-day text-right ${d === today ? 'dr-col-day--today' : ''}`}>
                      {dayHeaderLabel(d)}
                    </th>
                  ))}
                  <th className="dr-col-total text-right" title="SETUP|REABASTECER">Total</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const overdueFlag = (row.overdue?.setup || 0) !== 0 || (row.overdue?.restock || 0) !== 0;
                  return (
                    <tr key={rowKey(row)}>
                      <td>
                        <span className="pt-prep-code">{row.product_code}</span>
                        {row.product_description && (
                          <span className="pt-prep-desc">{row.product_description}</span>
                        )}
                      </td>
                      <td className="pt-mono">{row.variant_code || '—'}</td>
                      <td className="pt-mono">{row.lot_no || '—'}</td>
                      <td className="text-right pt-mono">
                        {fmtStock(row.nav_stock) || <span className="text-muted">—</span>}
                      </td>
                      <td className={`text-right pt-mono dr-cell-overdue ${overdueFlag ? 'dr-cell-overdue--flag' : ''}`}>
                        {fmtPair(row.overdue?.setup, row.overdue?.restock) || <span className="text-muted">—</span>}
                      </td>
                      {days.map((d) => (
                        <td key={d} className={`text-right pt-mono dr-cell ${d === today ? 'dr-col-day--today' : ''}`}>
                          {fmtPair(row.daily?.[d]?.setup, row.daily?.[d]?.restock) || <span className="text-muted">—</span>}
                        </td>
                      ))}
                      <td className="text-right pt-mono dr-cell-total">
                        {fmtPair(row.row_total?.setup, row.row_total?.restock) || <span className="text-muted">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="toast-container">
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            id={toast.id}
            message={toast.message}
            type={toast.type}
            duration={toast.duration}
            requireConfirm={toast.requireConfirm}
            onConfirm={toast.onConfirm}
            onClose={removeToast}
          />
        ))}
      </div>
    </div>
  );
};

export default DailyRequirements;